// ADR-099 follow-up — Architecture D: this plugin is now a pure SENTINEL SPLIT
// (no position/offset math). insertCitationSentinels (attributionSentinels.ts)
// injects `\uE000{spanArrayIndex}\uE001` into the answer STRING before parsing,
// so remark places the sentinel inside the correct text node natively — working
// inside bullets / GFM table cells / headings / strong / em, the structural
// boundaries the old offset-into-hast walk silently dropped. Here we walk every
// text node, split on the sentinel, and resolve the encoded array index back to
// spans[i] to emit the <cite-chip> element (the render half — cite-chip ->
// CitationChip pill via the components adapter — is unchanged).

import { SENTINEL_OPEN } from "./attributionSentinels";

export interface AttributionSpan {
  utf16End: number;
  chunkId: string;
  index: number;
}

interface HastText {
  type: "text";
  value: string;
}

interface HastElement {
  type: "element";
  tagName: string;
  properties?: Record<string, unknown>;
  children: HastNode[];
}

type HastNode = HastText | HastElement;

interface Root {
  type: "root";
  children: HastNode[];
}

const SENTINEL_RE = /\uE000(\d+)\uE001/g;

function makeChip(span: AttributionSpan): HastElement {
  return {
    type: "element",
    tagName: "cite-chip",
    properties: { dataChunkId: span.chunkId, dataIndex: String(span.index) },
    children: [],
  };
}

// Split a sentinel-bearing text value into [text, cite-chip, text, ...].
// An out-of-range / missing span index is SILENTLY CONSUMED (sentinel removed,
// no chip) so a stale span can never leak raw PUA characters into the DOM.
function splitTextNodeBySentinel(value: string, spans: AttributionSpan[]): HastNode[] {
  const out: HastNode[] = [];
  const re = new RegExp(SENTINEL_RE); // fresh lastIndex per call
  let last = 0;
  let m = re.exec(value);
  while (m !== null) {
    if (m.index > last) out.push({ type: "text", value: value.slice(last, m.index) });
    const span = spans[Number(m[1])];
    if (span) out.push(makeChip(span));
    last = m.index + m[0].length;
    m = re.exec(value);
  }
  if (out.length === 0) return [{ type: "text", value }]; // malformed/partial sentinel → passthrough
  if (last < value.length) out.push({ type: "text", value: value.slice(last) });
  return out;
}

// Rebuild children into a fresh array (never splice in place): produced nodes are
// appended to the NEW array while we iterate the ORIGINAL children, so a freshly
// injected chip can never be re-walked or re-split.
function walk(node: Root | HastElement, spans: AttributionSpan[]): void {
  const newChildren: HastNode[] = [];
  for (const child of node.children) {
    if (child.type === "text") {
      if (child.value.includes(SENTINEL_OPEN)) {
        for (const produced of splitTextNodeBySentinel(child.value, spans)) newChildren.push(produced);
      } else {
        newChildren.push(child);
      }
    } else if (child.type === "element") {
      walk(child, spans);
      newChildren.push(child);
    } else {
      newChildren.push(child);
    }
  }
  node.children = newChildren;
}

export function rehypeAttributionChips(opts: {
  spans: AttributionSpan[];
}): (tree: Root) => void {
  const { spans } = opts;
  return (tree: Root): void => {
    if (spans.length === 0) {
      return; // cohort-isolation: nothing to inject, tree byte-identical
    }
    walk(tree, spans);
  };
}
